import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarClock, Loader2, Trophy, Users, Vote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import TopBadgeApply from "@/components/top-badge/TopBadgeApply";
import TopBadgeEntryCard from "@/components/top-badge/TopBadgeEntryCard";
import TopBadgeCountdown from "@/components/top-badge/TopBadgeCountdown";
import TopBadgeMonthlyGoals from "@/components/top-badge/TopBadgeMonthlyGoals";
import TopBadgeShareButton from "@/components/top-badge/TopBadgeShareButton";

const ET = { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" };
const fmt = (iso) => new Date(iso).toLocaleString("en-US", ET);
const PHASE = {
  upcoming: { label: "Launching soon", cls: "border-zinc-500 text-zinc-300" },
  submissions: { label: "Submissions & voting open", cls: "border-emerald-400 text-emerald-300" },
  voting: { label: "Voting open — submissions closed", cls: "border-yellow-400 text-yellow-200" },
  closed: { label: "Voting closed", cls: "border-rose-400 text-rose-300" },
  awarded: { label: "Voting closed · winners announced", cls: "border-yellow-400 text-yellow-200" },
};

export default function TopBadgeCampaignPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const sharedEntryId = params.get("entry");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/top-badge/campaign").then((r) => setData(r.data)).catch(() => toast.error("Could not load the campaign.")), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data || !sharedEntryId) return;
    const el = document.getElementById(`entry-${sharedEntryId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    else toast.info("That entry is no longer in the running.");
  }, [data, sharedEntryId]);

  const vote = async (entryId) => {
    setBusy(true);
    try {
      await api.post("/top-badge/votes", { entry_id: entryId });
      toast.success("Vote counted — thank you!");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record your vote."); }
    finally { setBusy(false); }
  };

  const withdraw = async () => {
    if (!window.confirm("Withdraw your entry? Votes you've received will be removed.")) return;
    try { await api.delete("/top-badge/entries/me"); toast.success("Entry withdrawn."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not withdraw."); }
  };

  if (!data) return <div className="p-10 text-center text-zinc-500" data-testid="top-badge-loading"><Loader2 className="animate-spin inline" /></div>;
  const ph = data.awarded ? PHASE.awarded : (PHASE[data.phase] || PHASE.upcoming);
  const canVote = data.logged_in && !data.awarded && (data.phase === "submissions" || data.phase === "voting");
  const hasVoted = !!data.my_vote_entry_id;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="top-badge-campaign">
      <header className="card-surface p-5 sm:p-7 flex flex-col sm:flex-row gap-5 items-start">
        <img src="/vip-art/top_badge.png" alt="Top Badge" className="w-28 h-28 sm:w-36 sm:h-36 object-contain drop-shadow-[0_6px_24px_rgba(250,204,21,0.35)] shrink-0" draggable={false} />
        <div className="space-y-3 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300">Top Badge Kickoff</div>
          <h1 className="font-display text-3xl sm:text-4xl text-white leading-tight">Top 5 creators earn the gold Top Badge</h1>
          <span className={`inline-block border px-2 py-0.5 text-[11px] uppercase tracking-widest ${ph.cls}`} data-testid="top-badge-phase">{ph.label}</span>
          <ul className="text-sm text-zinc-300 space-y-1" data-testid="top-badge-rules">
            <li><CalendarClock size={13} className="inline mr-1.5 text-yellow-300" />Launches <b className="text-white">{fmt(data.launch_at)}</b></li>
            <li><CalendarClock size={13} className="inline mr-1.5 text-yellow-300" />Submit your 15-second video by <b className="text-white">{fmt(data.submissions_close_at)}</b></li>
            <li><Vote size={13} className="inline mr-1.5 text-yellow-300" />Every member gets <b className="text-white">one vote</b> · voting closes <b className="text-white">{fmt(data.voting_close_at)}</b></li>
            <li><Trophy size={13} className="inline mr-1.5 text-yellow-300" />Top {data.winners} by votes earn the badge instantly — ties at the cutoff all win. Kickoff winners are exempt from monthly goals through Dec 31, 2026.</li>
            <li><Users size={13} className="inline mr-1.5 text-yellow-300" />Anyone can apply. From November, creators earn or keep the badge monthly: 25 streaming hours + 50,000 🎋 from gifts (details below).</li>
          </ul>
          <div className="flex gap-4 text-xs text-zinc-400"><span data-testid="top-badge-entry-count"><b className="text-white">{data.entry_count}</b> entries</span><span data-testid="top-badge-vote-count"><b className="text-white">{data.vote_count}</b> votes cast</span></div>
        </div>
      </header>

      {!data.awarded && <TopBadgeCountdown launchAt={data.launch_at} submissionsCloseAt={data.submissions_close_at} votingCloseAt={data.voting_close_at} />}

      {!data.logged_in && (
        <div className="card-surface p-4 flex items-center justify-between gap-3 text-sm text-zinc-300" data-testid="top-badge-login-cta">Log in to apply or cast your vote.<Button asChild size="sm" className="bg-yellow-400 text-black hover:bg-yellow-300"><Link to="/login">Log in</Link></Button></div>
      )}
      {data.logged_in && data.phase === "submissions" && !data.my_entry_id && <TopBadgeApply maxSeconds={data.max_video_seconds} onSubmitted={load} />}
      {data.logged_in && data.my_entry_id && (
        <div className="card-surface p-4 flex flex-wrap items-center justify-between gap-3 text-sm text-zinc-300" data-testid="top-badge-my-entry">You&apos;re in the running — share your link to rally votes.<div className="flex gap-2"><TopBadgeShareButton entryId={data.my_entry_id} name={user?.display_name} className="border border-yellow-400/50 text-yellow-200 hover:bg-yellow-400/10" label="Copy share link" />{data.phase === "submissions" && <Button size="sm" variant="ghost" onClick={withdraw} className="text-rose-300" data-testid="top-badge-withdraw">Withdraw entry</Button>}</div></div>
      )}
      {hasVoted && <div className="text-xs text-yellow-200" data-testid="top-badge-voted-notice">You&apos;ve cast your one vote. Thank you!</div>}
      {data.awarded && (
        <section className="border border-yellow-400/60 bg-yellow-400/10 p-5" data-testid="top-badge-winners">
          <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-2">Kickoff winners · Top Badge awarded</div>
          {data.winner_entries.length === 0 ? <div className="text-sm text-zinc-300">No entries received votes, so no badges were awarded.</div> : (
            <ul className="flex flex-wrap gap-3">
              {data.winner_entries.map((w) => (
                <li key={w.id} data-testid={`top-badge-winner-${w.user.id}`}><Link to={`/u/${w.user.id}`} className="inline-flex items-center gap-2 border border-yellow-400/50 bg-black/40 px-3 py-1.5 text-sm text-white hover:bg-yellow-400/10"><img src="/vip-art/top_badge.png" alt="" className="w-5 h-5 object-contain" />{w.user.display_name}<span className="text-xs text-yellow-200 tabular-nums">{w.votes} vote{w.votes === 1 ? "" : "s"}</span></Link></li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Entries · ranked by votes</div>
        {data.entries.length === 0 ? (
          <div className="card-surface p-8 text-center text-sm text-zinc-500" data-testid="top-badge-no-entries">No entries yet{data.phase === "submissions" ? " — be the first." : "."}</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="top-badge-entries">
            {data.entries.map((e, i) => (
              <TopBadgeEntryCard key={e.id} entry={e} rank={i + 1} canVote={canVote} hasVoted={hasVoted} votedHere={data.my_vote_entry_id === e.id} onVote={vote} busy={busy} highlighted={e.id === sharedEntryId} />
            ))}
          </div>
        )}
      </section>

      <TopBadgeMonthlyGoals />
      {user?.role === "admin" && <div className="text-[11px] text-zinc-600">Admin: remove an entry via DELETE /api/top-badge/admin/entries/:id</div>}
    </div>
  );
}
